// chat_widget/src/widget/hooks/useBadge.ts
import { useEffect, useMemo, useState } from "react";
import type { Msg } from "../types/messagesTypes";

type UseBadgeArgs = {
  userId: string;
  sessionId: string;
  open: boolean;
  messages: Msg[];
};

export function useBadge({ userId, sessionId, open, messages }: UseBadgeArgs) {
  const storageKey = useMemo(
    () => `vcw_last_read_${userId}_${sessionId}`,
    [userId, sessionId],
  );

  // 1. On initialise le "dernier message lu" DIRECTEMENT depuis le localStorage.
  // Au rafraîchissement de la page, l'information est immédiatement là, pas de délai.
  const [lastReadId, setLastReadId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(storageKey);
    } catch {
      return null;
    }
  });

  // 2. Vérifie si l'utilisateur a envoyé au moins un message
  const hasUserSentMessage = useMemo(() => {
    return messages.some((m) => m.role === "user");
  }, [messages]);

  // 3. Identifie le dernier message du bot de façon toujours aussi robuste
  const lastSystemMessageId = useMemo(() => {
    const nonUserMessages = messages.filter((m) => m.role !== "user");
    if (nonUserMessages.length === 0) return null;

    const lastMsg = nonUserMessages[nonUserMessages.length - 1] as any;
    return lastMsg.id || lastMsg._id || `sys_msg_${nonUserMessages.length}`;
  }, [messages]);

  // 4. On met à jour le "dernier lu" UNIQUEMENT si le chat est ouvert et qu'il y a un message
  useEffect(() => {
    if (open && lastSystemMessageId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLastReadId(lastSystemMessageId);
      try {
        localStorage.setItem(storageKey, lastSystemMessageId);
      } catch {
        // silent
      }
    }
  }, [open, lastSystemMessageId, storageKey]);

  // 5. La logique d'affichage devient un simple calcul basé sur les variables ci-dessus
  const showBadge = useMemo(() => {
    // Condition 1 : L'utilisateur n'a jamais envoyé de messages
    if (!hasUserSentMessage) {
      return true;
    }

    // Condition 2 : Le modal est fermé ET on a reçu un message différent de celui qu'on a lu
    if (!open && lastSystemMessageId && lastSystemMessageId !== lastReadId) {
      return true;
    }

    return false;
  }, [hasUserSentMessage, open, lastSystemMessageId, lastReadId]);

  return { showBadge };
}
